import { useMutation, useQuery } from '@apollo/client';
import {
  CardPage,
  getDateOperators,
  getDefaultValue,
  getTranslatedText,
  toast,
  ALL_TABLES,
  getAllColumnWidth,
  getColumnWidth as superTableColumnWidth,
  getPersonalizedSorting,
  useCheckFeatureGating,
  useActivityTracker,
  useSuperListHook,
  ResetFilter,
  getCurrentUserHook,
  AlertOccurrent,
  AlertOccurrencesModal
} from '@nile/components';
import { useAlertOccurrencesModal } from '@nile/rmm/src/modules/alert-management/hooks/useAlertOccurrencesModal';
import {
  RMMClient,
  AccountClient,
  GET_PERSONALIZATION_BY_NAME
} from '@nile/graphql';
import { useChatFeatureEnabled } from '@nile/components/src/Pages/SystemTrayChatPage/utils';
import {
  GET_ALERT_DETAILS_META_INFO_V2,
  GET_ALERT_META_INFO_V4,
  MOVE_ALERT_TO_TICKET,
  RESOLVE_ALERT,
  RESOLVE_ALERTS,
  DELETE_ALERTS
} from '@nile/rmm/src/modules/alert-management/Graphql/alert';
import {
  checkDynamicQuery,
  columnsConfig,
  fetchValues,
  generateOptions,
  getAccessor,
  getAsyncOptions,
  getAttributeName,
  getColumnWidth,
  getDynamicQuery,
  getMeta,
  getTableVariables,
  getType
} from '@nile/rmm/src/modules/alert-management/services/alert-list-helper.service';
import { getProcessedConfig } from '@nile/rmm/src/modules/common/services/tableHelper';
import { Heading, Page } from '@superops/ui-design-system';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState, useContext } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import AlertBulkAction from '@nile/rmm/src/modules/alert-management/components/sub-pages/alert-management-view-list/alert-bulk-action';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import { assetDetailsTab } from '../../../../alert-management/constants/constants';
import AlertsTable from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-alerts/AlertsTable';

interface AlertAsset {
  assetId: string;
  name: string;
  assetClass?: {
    classId: string;
    name?: string;
  };
  client?: {
    name: string;
    accountId: string;
  };
  site?: {
    name: string;
    id: string;
  };
}

interface AlertPolicy {
  name: string;
  policyType?: string;
}

interface AlertRow {
  original: {
    id: string;
    message: string;
    severity: string;
    status: string;
    description: string;
    createdTime: string;
    resolvedTime?: string;
    occurrenceCount: number;
    asset: AlertAsset;
    policy: AlertPolicy;
  };
}

const AssetAlerts = (props) => {
  const {
    entitiesMeta,
    intl,
    defaultCondition,
    flags,
    tableType,
    assetClass,
    isTableEditable = false,
    noBulkSelect = false
  } = props;

  const assetId = props?.match?.params?.id;
  const [alertList, setAlertList] = useState([]);
  const [alertListLoading, setAlertListLoading] = useState(true);
  const tabContext = useContext(TicketContext);
  const isTicketingAvailable = useCheckFeatureGating('TICKETING')?.enabled;

  // Bulk action context functions
  const [enalbeResolveAction, setEnalbeResolveAction] = useState(false);

  const [listLoader, setlistLoader] = useState(false);
  const [columnGroup, setColumnGroup] = useState(null);

  const user = getCurrentUserHook();
  const tableName = ALL_TABLES.assetAlert;
  const columnWidthObject = getAllColumnWidth(tableName);
  const personalizedSorting = getPersonalizedSorting(tableName);
  const isChatAvailable = useChatFeatureEnabled();

  const {
    records,
    setRecords,
    tableCondition,
    updateTableFilterCondition
  } = useSuperListHook({ tableName, viewId: assetId });

  const fetchPersonalization = async () => {
    const response = await AccountClient.query({
      query: GET_PERSONALIZATION_BY_NAME,
      fetchPolicy: 'network-only',
      variables: {
        userId: user.userId,
        category: 'AlertView',
        name: '1013'
      }
    });

    return response.data.getPersonalizationByCategoryAndName?.value
      ? JSON.parse(response.data.getPersonalizationByCategoryAndName?.value)
      : [];
  };

  const fetchMetaInfoQuery = async () => {
    const response = await RMMClient.query({
      query:
        tableType === 'reportTableTable'
          ? GET_ALERT_META_INFO_V4
          : GET_ALERT_DETAILS_META_INFO_V2,
      fetchPolicy: 'network-only'
    });
    const getAlertsForAssetMetaInfo =
      response.data.getAlertsForAssetMetaInfoV2 ||
      response.data.getAlertViewMetaInfoV4;
    return getAlertsForAssetMetaInfo.defaultSelectedAttributes;
  };

  const generateColumns = async () => {
    setlistLoader(true);
    const defaultColumns = await fetchMetaInfoQuery();
    const columnPersonalization =
      tableType === 'reportTableTable' ? await fetchPersonalization() : null;
    const { configuredCols } = getProcessedConfig({
      entitiesMeta,
      allColumns: defaultColumns,
      intl,
      module: 'Alert',
      columnPersonalization:
        tableType === 'reportTableTable' ? columnPersonalization : null
    });
    constructTableData(configuredCols);
    setAlertListLoading(false);
    setlistLoader(false);
  };

  useEffect(() => {
    if (!columnGroup) {
      generateColumns();
    }
  }, [entitiesMeta, defaultCondition, assetId]);

  const [moveAlertToticket] = useMutation(MOVE_ALERT_TO_TICKET, {
    client: RMMClient,
    onCompleted() {
      toast('success', 'asset.details.alertViews.alertMovedToTicket.success');
      generateColumns();
    },
    onError() {
      toast('error', 'something went wrong');
    }
  });

  const moveAlertToticketsAndInitiateChat = (variables, isChat) => {
    moveAlertToticket({
      ...variables
    }).then((res) => {
      sessionStorage.setItem('OpenChat', true);
      toast('success', 'rmm.alertManagement.alertToTicket.successMsg');
      tabContext.openTab({
        module: 'TICKET',
        data: {
          ticketId: res.data.convertAlertToTicket
        }
      });
      useActivityTracker({
        event: 'INITIATE_CHAT - ALERT'
      });
    });
  };

  const moveToTicket = (obj, chat) => {
    const { id: alertId } = obj;
    if (chat && flags?.agentChatFe && isChatAvailable) {
      moveAlertToticketsAndInitiateChat(
        {
          variables: {
            alertId
          }
        },
        true
      );
    } else {
      moveAlertToticket({
        variables: {
          alertId
        }
      });
    }
  };

  const resolveAlert = (obj) => {
    const { id: alertId } = obj;
    resolveRespectiveAlert({
      variables: {
        alertId
      }
    });
  };

  const [resolveRespectiveAlert] = useMutation(RESOLVE_ALERT, {
    client: RMMClient,
    onCompleted() {
      toast('success', 'asset.details.alertViews.alertResolved.sucess');
      generateColumns();

      useActivityTracker({
        event: 'Alert - Resolved'
      });
    },
    onError() {
      toast('error', 'something went wrong');
    }
  });

  const navToDetailsPage = (obj) => {
    const { assetId, name, assetClass } = obj?.row?.original?.asset;
    tabContext.openTab({
      module: assetDetailsTab,
      data: {
        id: assetId,
        subject: name,
        classId: assetClass?.classId
      }
    });
  };

  const performActions = (col) => {
    switch (col.name) {
      case 'moreActions':
        return {
          onClick: moveToTicket
        };
      case 'status':
        return {
          onClick: resolveAlert
        };
      case 'asset.name':
        return {
          onClick: navToDetailsPage
        };
      case 'occurrenceCount':
        return {
          onViewOccurrences: handleViewOccurrences
        };
      default:
        break;
    }
  };

  const constructTableData = (cols) => {
    let columns = [];
    /** Remove the ticket creation option for RMM only plans */
    if (tableType === 'assetTable' && isTicketingAvailable) {
      columns.push({
        header: ' ',
        accessorKey: 'value',
        size: 30,
        Header: ' ',
        name: 'moreActions',
        accessor: 'value',
        width: 30,
        type: 'ACTIONS',
        disableSortBy: true,
        disableFilters: true,
        disableResizing: true,
        meta: {
          assetClass: assetClass
        },
        actions: performActions({
          name: 'moreActions'
        })
      });
    }
    const { intelligentAlertingForHierarchicalPolicy, acronis } = flags || {};

    // Occurrences column with AlertOccurrent component for tableType === 'assetTable'
    // (Asset Table uses meta info v2, hence does not have occurrenceCount in the columns,
    // whereas reportTableTable uses meta info v4, hence has occurrenceCount in the columns)
    if (tableType === 'assetTable') {
      columns.push({
        header: getTranslatedText(
          intl,
          'rmm.alert.occurrences.column.occurrences'
        ),
        accessorKey: 'occurrenceCount',
        size: getColumnWidth('occurrences'),
        minSize: getColumnWidth('occurrences', true),
        Header: getTranslatedText(
          intl,
          'rmm.alert.occurrences.column.occurrences'
        ),
        name: 'occurrenceCount',
        accessor: 'occurrenceCount',
        width: getColumnWidth('occurrences'),
        minWidth: getColumnWidth('occurrences', true),
        disableSortBy: false,
        disableFilters: true,
        field: { name: 'occurrenceCount' },
        cell: ({ row }: { row: AlertRow }) => {
          const alertData = row?.original;
          if (alertData?.occurrenceCount) {
            return (
              <AlertOccurrent
                count={alertData.occurrenceCount}
                onViewClick={() => handleViewOccurrences(alertData)}
              />
            );
          }
          return null;
        }
      });
    }

    cols.map((col, idx) => {
      columns.push({
        header: col.label,
        accessorKey: getAccessor(col),
        minSize: getColumnWidth(col.name, true),
        size: superTableColumnWidth(
          columnWidthObject,
          col.name,
          getColumnWidth(col.name)
        ),
        Header: getTranslatedText(intl, col?.label.toLowerCase()),
        minWidth: getColumnWidth(col.name, true),
        width: getColumnWidth(col.name),
        accessor: getAccessor(col),
        type: getType(col),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        disableSortBy: tableType === 'assetTable' ? false : true,
        editable: isTableEditable,
        disableFilters: tableType === 'assetTable' ? false : true,
        meta: { ...getMeta(col), noAction: tableType !== 'assetTable' },
        field: {
          ...col,
          label: getTranslatedText(intl, col?.label.toLowerCase())
        },
        isBold: col.name === 'message' ? true : false,
        actions: performActions(col),
        helpers: {
          getDefaultValue,
          checkDynamicQuery,
          updateConditions: updateTableFilterCondition,
          getDateOperators,
          getVariables: getTableVariables.bind(this, col),
          getDynamicQuery,
          generateOptions,
          fetchValues,
          getAsyncOptions
        }
      });
    });

    setColumnGroup(columns);
  };

  useEffect(() => {
    if (records && records?.length > 0) {
      setEnalbeResolveAction(true);
    } else {
      setEnalbeResolveAction(false);
    }
  }, [records]);

  const [resolveRespectiveAlerts] = useMutation(RESOLVE_ALERTS, {
    client: RMMClient,
    onCompleted() {
      toast('success', 'rmm.alertManagement.bulkResolve.successMsg');
      setRecords([]);
      generateColumns();
      useActivityTracker({
        event: 'Alert - Bulk alerts resolved'
      });
    }
  });

  const [deleteRespectiveAlerts] = useMutation(DELETE_ALERTS, {
    client: RMMClient,
    onCompleted() {
      toast('success', 'rmm.alertManagement.bulkDelete.successMsg');
      setRecords([]);
      generateColumns();
    }
  });

  // Alert Occurrences Modal hook
  const { handleViewOccurrences, modalProps } = useAlertOccurrencesModal({
    intl,
    showActionBtns: true,
    onResolveCompleted: () => {
      toast('success', 'asset.details.alertViews.alertResolved.sucess');
      generateColumns();
      useActivityTracker({
        event: 'Alert - Resolved'
      });
    },
    onResolveError: () => {
      toast('error', getTranslatedText(intl, 'rmm.assetView.update.errorMsg'));
    },
    onDeleteCompleted: () => {
      toast('success', 'rmm.alertManagement.bulkDelete.successMsg');
      setRecords([]);
      generateColumns();
    },
    onDeleteError: () => {
      toast('error', getTranslatedText(intl, 'rmm.assetView.update.errorMsg'));
    }
  });

  const onClickResolve = (actionType: string) => {
    if (actionType === 'RESOLVE') {
      resolveRespectiveAlerts({
        variables: {
          alertIds: records.map((item) => item.id)
        }
      });
    } else if (actionType === 'MOVE_TRASH') {
      deleteRespectiveAlerts({
        variables: {
          alertIds: records.map((item) => item.id)
        }
      });
    }
  };

  return (
    <>
      {tableType === 'assetTable' ? (
        <div className="full-width full-height position-relative p20 flex wrap g15">
          <CardPage
            className="full-height full-width white-grey-page"
            tableContent
            fullHeight
          >
            <Page
              variant="secondary"
              contentHeight="80px"
              title={
                <Heading element="h2">
                  <div className="fx">
                    <div className="header">
                      <FormattedMessage id="asset.details.alerts.title" />
                    </div>
                  </div>
                </Heading>
              }
              fullWidth
              primaryAction={
                <div className="flex flex-align-center">
                  <div className="mr-10">
                    <ResetFilter tableName={tableName} viewId={assetId} />
                  </div>
                  <AlertBulkAction
                    records={records}
                    actionClick={onClickResolve}
                    disableAction={!enalbeResolveAction}
                  />
                </div>
              }
            >
              <AlertsTable
                alertListLoading={alertListLoading}
                noBulkSelect={noBulkSelect}
                listLoader={listLoader}
                records={records}
                setRecords={setRecords}
                columnGroup={columnGroup}
                user={user}
                tableName={tableName}
                assetId={assetId}
                columnsConfig={columnsConfig}
                alertList={alertList}
                setAlertList={setAlertList}
                getAttributeName={getAttributeName}
                tableCondition={tableCondition}
                personalizedSorting={personalizedSorting}
                defaultCondition={defaultCondition}
                tableType={tableType}
              />
            </Page>
          </CardPage>
        </div>
      ) : (
        <AlertsTable
          alertListLoading={alertListLoading}
          noBulkSelect={noBulkSelect}
          listLoader={listLoader}
          records={records}
          setRecords={setRecords}
          columnGroup={columnGroup}
          user={user}
          tableName={tableName}
          assetId={assetId}
          columnsConfig={columnsConfig}
          alertList={alertList}
          setAlertList={setAlertList}
          getAttributeName={getAttributeName}
          tableCondition={tableCondition}
          personalizedSorting={personalizedSorting}
          defaultCondition={defaultCondition}
          tableType={tableType}
        />
      )}

      {/* Alert Occurrences Modal */}
      {modalProps.alertDetails && <AlertOccurrencesModal {...modalProps} />}
    </>
  );
};

export default withRouter(withLDConsumer()(injectIntl(AssetAlerts)));
